import {InputNumber, Select, TimePicker} from 'antd';
import {FeeScheduleTable} from 'components/feeSchedule/FeeScheduleTable';
import moment from 'moment';
import {FormCard, spans} from 'pages/AssetManagement/v2/FormCard';
import styles from './exchanges.module.scss';

/**
 * Per-instrument-type trading overrides for one exchange.
 *
 * The exchange form beside this one holds the DEFAULTS. An entry here is
 * sparse: it carries only what differs for this instrument type, and anything
 * unset falls through.
 *
 * Nothing here is pre-filled, and no field carries a badge or a placeholder
 * saying so. Blank means the exchange decides — the card says that once, at
 * the top, and repeating it on nine labels only crowded them.
 *
 * Clearing a field is how you hand it back: empty the box, or use the select's
 * own clear control. There is no separate reset action.
 *
 * It used to seed every control with the exchange's current value instead,
 * which was wrong in two ways. Visually, "unset" and "set to the same number
 * as the exchange" looked identical. Worse, the seeded value was live form
 * state: touching one fee cell promoted the whole copied schedule into a real
 * override, so the type silently froze a snapshot of the exchange's fees.
 * Change the exchange afterwards and this tab still showed — and saved — the
 * old numbers, which is exactly the "it doesn't load what was updated"
 * complaint.
 *
 * ⚠️ The rule underneath all of that: **clearing a field means inherit, it
 * does not mean off or zero.** `false` and `0` are real overrides — "limit
 * orders are off for funds" and "settles same day" both have to be
 * expressible — which is why the draft distinguishes an absent field from a
 * falsy one.
 *
 * Verified against the dev gateway: writing `enableLimitOrder: false` and
 * `estimatedProcessingDays: 0` removed both from `inheritedFields`, while the
 * fields left unset stayed in it.
 */

/** The nine overridable fields, in the order the exchange form shows them. */
export const OVERRIDABLE_FIELDS = [
  'tradeable',
  'enableMarketOrder',
  'enableLimitOrder',
  'fractionalShares',
  'orderExpirationLimit',
  'orderProcessingCutoffTime',
  'estimatedSettlementDays',
  'estimatedProcessingDays',
  'feeSchedule',
];

/**
 * Whether one field of a draft entry is a real override.
 *
 * `null` means inherit, so it does not count — but `false` and `0` do, which
 * is why this is not a truthiness test. An empty fee schedule is the array
 * form of empty and inherits too.
 */
export const isRealOverride = value => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/** How many fields of a draft entry actually differ from the exchange. */
export const countOverrides = entry =>
  OVERRIDABLE_FIELDS.filter(field => isRealOverride(entry?.[field])).length;

const parseTime = value => {
  if (!value) return null;
  const parsed = moment(value, ['HH:mm', 'H:m', 'HH:mm:ss'], true);
  return parsed.isValid() ? parsed : null;
};

/** One field row: a label and the caller's control, nothing else. */
const Field = ({label, span = spans.third, children}) => (
  <div className={span}>
    <label className={styles.overrideLabel}>{label}</label>
    {children}
  </div>
);

export const ExchangeInstrumentConfigForm = ({
  instrumentKey,
  label,
  override,
  onChange,
  disabled,
}) => {
  // Writes one field of the override. `null` un-overrides it, which empties
  // the control and hands the field back to the exchange.
  const set = (field, value) =>
    onChange({...(override ?? {instrumentKey}), instrumentKey, [field]: value});

  /**
   * What the control shows: this type's own value, or nothing.
   *
   * Never the exchange's value. A control holding a borrowed number is one
   * edit away from saving it as this type's own.
   */
  const shown = field => override?.[field];

  /**
   * A switch cannot say "not set" — off and unset look the same, and off is a
   * real override here ("limit orders are disabled for funds"). So the three
   * states get three options.
   */
  const boolField = (field, label_) => (
    <Field label={label_}>
      <Select
        disabled={disabled}
        value={shown(field) ?? undefined}
        onChange={value => set(field, value ?? null)}
        allowClear
        style={{width: '100%'}}
        options={[
          {value: true, label: 'On'},
          {value: false, label: 'Off'},
        ]}
      />
    </Field>
  );

  const numberField = (field, label_) => (
    <Field label={label_}>
      <InputNumber
        min={0}
        disabled={disabled}
        value={shown(field) ?? undefined}
        // Emptying the box is the same as pressing clear: antd gives null,
        // which drops the override and leaves the field empty again.
        onChange={value => set(field, value === undefined ? null : value)}
        style={{width: '100%'}}
      />
    </Field>
  );

  // This type's own rows, or none. The table used to open pre-filled with a
  // copy of the exchange's schedule, and because any edit promotes the whole
  // list, one changed cell froze the exchange's other rows here forever.
  const shownFees = override?.feeSchedule ?? [];

  return (
    <>
      <FormCard
        title={`${label} overrides`}
        description={`Anything you leave blank here uses whatever is set at the exchange level, and keeps following it when that changes. Fill a field in and it applies to ${label.toLowerCase()} on this exchange only.`}>
        {boolField('tradeable', 'Tradeable')}
        {boolField('enableMarketOrder', 'Market orders')}
        {boolField('enableLimitOrder', 'Limit orders')}
        {boolField('fractionalShares', 'Fractional shares')}
        {numberField('orderExpirationLimit', 'Order expiry (days)')}
        <Field label="Cutoff time">
          <TimePicker
            format="HH:mm"
            minuteStep={1}
            disabled={disabled}
            // antd fills in "Select time" otherwise, which would be the only
            // control on the card carrying any text while empty.
            placeholder=""
            value={parseTime(shown('orderProcessingCutoffTime'))}
            onChange={value =>
              set(
                'orderProcessingCutoffTime',
                value ? moment(value).format('HH:mm') : null,
              )
            }
            style={{width: '100%'}}
          />
        </Field>
        {numberField('estimatedSettlementDays', 'Settles in (days)')}
        {numberField('estimatedProcessingDays', 'Processing (days)')}
      </FormCard>

      <FormCard
        title={`${label} fees`}
        description="Empty means this type charges the exchange's fee schedule. Add a row and this list replaces it wholesale for this type — the two are not merged, so it has to carry every fee that should apply.">
        <Field span={spans.full} label="Fee schedule">
          <FeeScheduleTable
            disabled={disabled}
            value={shownFees}
            // Any edit — a cell, an added row, a removed one — promotes the
            // whole list to an override, because that is the only shape the
            // API accepts. Removing every row leaves it empty, which inherits.
            onChange={next => set('feeSchedule', next)}
          />
        </Field>
      </FormCard>
    </>
  );
};

export default ExchangeInstrumentConfigForm;
